import { ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { corridorRegistry, POPULAR_ROUTE_SLUGS } from "@/data/corridors/registry";
import { ROUTE_PATHS, corridorPath } from "@/routes/routePaths";

const HIDDEN_PREFIXES = ["/dashboard", "/account", "/delete-account", "/webview-init"];

/** First two rows in the marketing grid use ↔ (longer intercity pairs already branded that way). */
const BIDIRECTIONAL_SLUGS = new Set(["hyderabad-vijayawada", "hyderabad-guntur"]);

export function PopularRoutesPreFooter() {
  const { pathname } = useLocation();

  if (HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return null;
  }

  const routes = POPULAR_ROUTE_SLUGS.map((slug) => corridorRegistry[slug]).filter(
    (r): r is NonNullable<typeof r> => r != null
  );

  if (routes.length === 0) {
    return null;
  }

  return (
    <section
      id="popular-routes"
      className="border-t border-border bg-gradient-to-b from-muted/40 to-muted/10 py-4 sm:py-5"
      style={{ scrollMarginTop: "100px" }}
    >
      <div className="container">
        <div className="mb-2 flex flex-col gap-2 sm:mb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Popular Rides
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to={ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              Intercity hub <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={ROUTE_PATHS.POPULAR_ROUTES}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:underline"
            >
              All routes
            </Link>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-primary/15 bg-background shadow-[0_10px_30px_rgba(18,30,22,0.06)]">
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4">
            {routes.map((route) => (
              <li
                key={route.slug}
                className="border-b border-border/70 sm:border-r sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:[&:nth-child(4n)]:border-r-0"
              >
                <Link
                  to={corridorPath(route.slug)}
                  className="group flex h-full min-h-[2.5rem] items-center px-3 py-2 text-left transition-colors hover:bg-primary/5 sm:px-4"
                >
                  <p className="min-w-0 text-[13px] font-semibold text-foreground underline-offset-4 group-hover:text-primary group-hover:underline sm:text-sm">
                    {route.cityA}{" "}
                    {BIDIRECTIONAL_SLUGS.has(route.slug) ? (
                      <span className="text-muted-foreground">↔</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}{" "}
                    {route.cityB}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
